import {
  Dispatch,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

interface useControlledTimerProps {
  timeout?: number;
  callbackTrigger: () => void;
}

export enum TimerStatus {
  Running = 'running',
  Paused = 'paused',
  Stopped = 'stopped',
}

export default function useControlledTimer(
  props: useControlledTimerProps
): [Dispatch<'play' | 'pause' | 'restart' | 'stop'>, TimerStatus | undefined] {
  const [status, setStatus] = useState<TimerStatus>();
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const timeLeft = useRef<number>(0);
  const endDate = useRef<number>(0);

  const repeat = useCallback(
    (timeout: number | undefined = props.timeout) => {
      if (!timeout) return;
      // Setando o Final para referência
      endDate.current = new Date().getTime() + timeout;
      // Chamando a função!
      timeoutRef.current = setTimeout(() => {
        props.callbackTrigger();
        repeat(props.timeout);
      }, timeout);
    },
    [props]
  );

  const dispatchAction = useCallback(
    (action: 'play' | 'pause' | 'restart' | 'stop') => {
      const now = new Date().getTime();
      switch (action) {
        case 'play':
          if (timeoutRef.current) {
            throw new Error('Timer já está em andamento');
          }
          setStatus(TimerStatus.Running);
          repeat(timeLeft.current > 200 ? timeLeft.current : props.timeout);
          timeLeft.current = 0;
          break;
        case 'pause':
          if (!timeoutRef.current) {
            throw new Error('Timer está parado!');
          }
          // Limpando referência do timeout
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = undefined;
          setStatus(TimerStatus.Paused);
          // Calculando Timeleft
          if (endDate.current && endDate.current > now) {
            timeLeft.current = endDate.current - now;
          }
          break;
        case 'restart':
          // Limpando referência do timeout
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = undefined;
          endDate.current = 0;
          repeat(props.timeout);
          setStatus(TimerStatus.Running);
          break;
        default:
        case 'stop':
          // Limpando referência do timeout
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          setStatus(TimerStatus.Stopped);
          timeoutRef.current = undefined;
          endDate.current = 0;
      }
    },
    [props.timeout, repeat]
  );

  useLayoutEffect(() => {
    if (props.timeout) {
      dispatchAction('play');
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // }, [timeoutRef])
  // useLayoutEffect(() => {
  //   const interval = setTimeout(() => {}, props.timeout)
  //   return () => {
  //     if (interval) clearInterval(interval)
  //   }
  // }, [])
  return [dispatchAction, status];
}
